import { useEffect, useState, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Save, Rocket, History, RotateCcw, Loader2, Maximize2, Code2 } from "lucide-react";

// Admin → How to Use → Page Design. Mirrors the Web Design code-workspace layout
// (code / split / preview modes, live sandboxed preview, Save / Publish / Version History).
const CODE_TABS = [
  { key: "html", label: "HTML", lang: "html" },
  { key: "css", label: "CSS", lang: "css" },
  { key: "javascript", label: "JavaScript", lang: "javascript" },
];

export default function HowToUseAdmin() {
  const [tab, setTab] = useState("html");
  const [mode, setMode] = useState("split"); // code | split | preview
  const [code, setCode] = useState({ html: "", css: "", javascript: "" });
  const [preview, setPreview] = useState("");
  const [liveVersion, setLiveVersion] = useState(null);
  const [curVersion, setCurVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.adminHowtoGet();
      const d = r.draft || {};
      setCode({ html: d.html || "", css: d.css || "", javascript: d.javascript || "" });
      setLiveVersion(r.live_version); setCurVersion(r.current_version); setVersions(r.versions || []);
    } catch { toast.error("Failed to load the How to Use page."); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doPreview = useCallback(async () => {
    try { const r = await api.adminHowtoPreview(code.html, code.css, code.javascript); setPreview(r.html); }
    catch { /* ignore */ }
  }, [code]);
  useEffect(() => { if (!loading && mode !== "code") doPreview(); }, [loading, mode, doPreview]);

  const saveDraft = async (silent) => {
    setBusy("save");
    try { const r = await api.adminHowtoSave(code.html, code.css, code.javascript); setSavedAt(new Date()); if (!silent) toast.success(`Saved v${r.version}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(null);
  };
  const publish = async () => {
    setBusy("pub");
    try { await api.adminHowtoSave(code.html, code.css, code.javascript); const r = await api.adminHowtoPublish(); toast.success(`Published live (v${r.live_version})`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    setBusy(null);
  };
  const rollback = async (v) => {
    if (!window.confirm(`Make version ${v} live again?`)) return;
    setBusy("rb");
    try { await api.adminHowtoRollback(v); toast.success(`Version ${v} is now live`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
    setBusy(null);
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-[#00F0FF]" /></div>;
  const curTab = CODE_TABS.find((t) => t.key === tab);

  return (
    <div data-testid="howto-admin" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-display text-lg font-black text-white"><Code2 size={18} className="text-[#00F0FF]" /> How to Use — Page Design</div>
          <p className="text-xs text-[#A1A1AA]">Authors the public <code className="text-[#00F0FF]">/how-to-use</code> page (sandboxed). {liveVersion ? `Live: v${liveVersion}` : "Not published yet — shows a coming-soon placeholder."}{curVersion ? ` · Draft: v${curVersion}` : ""}{savedAt ? ` · saved ${savedAt.toLocaleTimeString()}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => saveDraft(false)} disabled={busy === "save"} data-testid="howto-save-draft" className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/15 disabled:opacity-50">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
          <button onClick={publish} disabled={busy === "pub"} data-testid="howto-publish" className="flex items-center gap-1.5 rounded-lg bg-[#22c55e] px-3 py-1.5 text-xs font-bold text-black hover:brightness-110 disabled:opacity-50">{busy === "pub" ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} Publish</button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex gap-1 rounded-lg border border-white/10 bg-[#0D0D0E] p-1">
          {["code", "split", "preview"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`howto-mode-${m}`}
              className={`rounded-md px-3 py-1 text-xs font-semibold capitalize ${mode === m ? "bg-[#00F0FF] text-black" : "text-white/70 hover:bg-white/5"}`}>{m}</button>
          ))}
        </div>
        <button onClick={doPreview} data-testid="howto-preview" className="rounded-lg border border-white/15 px-2.5 py-1.5 text-xs text-white/70 hover:text-white">Refresh preview</button>
      </div>

      <div className={`grid gap-3 ${mode === "split" ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        {mode !== "preview" && (
          <div className="rounded-xl border border-white/10 bg-[#0D0D0E]" data-testid="howto-editor">
            <div className="flex gap-1 border-b border-white/10 p-2">
              {CODE_TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`howto-tab-${t.key}`}
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold ${tab === t.key ? "bg-[#00F0FF] text-black" : "text-white/70 hover:bg-white/5"}`}>{t.label}</button>
              ))}
            </div>
            <Editor height="440px" language={curTab?.lang} theme="vs-dark" value={code[tab]}
              onChange={(v) => setCode((c) => ({ ...c, [tab]: v ?? "" }))}
              options={{ minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, wordWrap: "on", padding: { top: 10 } }} />
          </div>
        )}
        {mode !== "code" && (
          <div className="overflow-hidden rounded-xl border border-white/10 bg-[#0D0D0E]" data-testid="howto-preview-panel">
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-xs text-white/60">
              <span>Live preview (sandboxed)</span>
              <button onClick={() => { const w = window.open("", "_blank"); if (w) { w.document.write(preview); w.document.close(); } }} className="text-white/60 hover:text-white"><Maximize2 size={13} /></button>
            </div>
            <iframe title="How to Use preview" data-testid="howto-preview-iframe" sandbox="allow-scripts allow-popups allow-forms" srcDoc={preview} className="h-[440px] w-full border-0 bg-white" />
          </div>
        )}
      </div>

      <div className="rounded-xl border border-white/10 bg-[#0D0D0E] p-4" data-testid="howto-versions">
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#A1A1AA]"><History size={14} /> Version History</div>
        {versions.length === 0 ? <p className="text-xs text-[#71717a]">No versions yet. Save a draft to create v1.</p> : (
          <div className="space-y-1.5">
            {versions.map((v) => (
              <div key={v.version} data-testid={`howto-version-${v.version}`} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-xs">
                <span className="flex items-center gap-2"><b className="text-white">v{v.version}</b>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${v.status === "live" ? "bg-[#22c55e]/20 text-[#22c55e]" : "bg-white/10 text-[#A1A1AA]"}`}>{v.status}</span>
                  <span className="text-[#71717a]">{v.created_by || ""}</span></span>
                {v.status !== "live" && (
                  <button data-testid={`howto-rollback-${v.version}`} onClick={() => rollback(v.version)} className="flex items-center gap-1 rounded-lg border border-white/15 px-3 py-1 font-semibold text-white hover:border-white/30"><RotateCcw size={12} /> Make live</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
